export type ZplLayoutItemKind = "text" | "graphic" | "barcode" | "box";

export type ZplLayoutItem = {
  id: string;
  name: string;
  kind: ZplLayoutItemKind;
  x: number;
  y: number;
  size: number;
  font: string;
  text: string;
};

export type ZplLayoutPatch = {
  id: string;
  name?: string;
  x?: number;
  y?: number;
  size?: number;
};

type ParsedField = {
  id: string;
  kind: ZplLayoutItemKind;
  x: number;
  y: number;
  size: number;
  fontRaw: string;
  text: string;
  start: number;
  end: number;
  prefixStart: number;
  w: number;
  h: number;
};

function displayFont(raw: string): string {
  if (!raw) return "";
  if (raw.includes("ROBOTOC") || /^\^AZ/.test(raw)) return "Roboto Condensed";
  if (raw.includes("ROBOTOL") || /^\^AL/.test(raw)) return "Roboto Condensed Light";
  if (raw.startsWith("^A0") || raw === "0") return "Zebra 0";
  const file = raw.match(/E:([A-Z0-9.]+)/i);
  if (file) return file[1];
  return raw.replace(/^\^A@[NRIB],/, "");
}

function fieldMetrics(
  body: string,
  fontH: number,
): {
  kind: ZplLayoutItemKind;
  size: number;
  w: number;
  h: number;
  text: string;
  fontRaw: string;
} {
  const fdRaw = body.match(/\^FD([\s\S]*?)\^FS/)?.[1] ?? "";
  const text = fdRaw.replace(/\\&/g, " ").replace(/\s+/g, " ").trim();
  const fontCmd = body.match(/\^A(?:@|Z|0|L)[NRIB],(\d+),(\d+)(?:,([^,\^]+))?/);
  const gfa = body.match(/\^GFA,(\d+),(\d+),(\d+),/);
  const bx = body.match(/\^BX[NRIB],(\d+),\d+,(\d+),(\d+)/);
  const gb = body.match(/\^GB(\d+),(\d+)/);
  const fb = body.match(/\^FB(\d+),(\d+)(?:,(-?\d+))?/);
  const fontRaw = fontCmd?.[0] ?? "";
  if (gfa) {
    const rowBytes = Number(gfa[3]);
    const h = Number(gfa[1]) / rowBytes;
    return { kind: "graphic", size: Math.round(h), w: rowBytes * 8, h, text: "Logo / graphic", fontRaw: "" };
  }
  if (bx) {
    const mag = Number(bx[1]);
    const cells = Number(bx[2]);
    const dim = mag * cells;
    return { kind: "barcode", size: mag, w: dim, h: dim, text: text || "Data Matrix", fontRaw: "" };
  }
  if (gb && !text) {
    const w = Number(gb[1]);
    const h = Number(gb[2]);
    return { kind: "box", size: Math.max(w, h), w, h, text: h <= 8 || w <= 8 ? "Line" : "Box", fontRaw: "" };
  }
  const size = Number(fontCmd?.[1]) || fontH || 20;
  const w = fb ? Number(fb[1]) : Math.max(40, Math.round(text.length * size * 0.55));
  const fdLines = Math.max(1, fdRaw.split(/\\&/).length);
  const lines = Math.max(fb ? Number(fb[2]) : 1, fdLines);
  const gap = fb && fb[3] ? Number(fb[3]) : 0;
  const h = size * lines + Math.max(0, lines - 1) * gap + Math.round(size * 0.45);
  return { kind: "text", size, w, h, text, fontRaw };
}

export function parseZplLayoutFields(zpl: string): ParsedField[] {
  const fields: ParsedField[] = [];
  const re = /\^FO(-?\d+),(-?\d+)([\s\S]*?)\^FS/g;
  let match: RegExpExecArray | null;
  let prevEnd = 0;
  let n = 0;
  let lastFontH = 20;
  let lastFontRaw = "";
  while ((match = re.exec(zpl))) {
    const between = zpl.slice(prevEnd, match.index);
    const prefixFont = between.match(/\^A(?:@|Z|0|L)[NRIB],(\d+),(\d+)(?:,([^,\s\^]+))?/g);
    if (prefixFont && prefixFont.length) {
      lastFontRaw = prefixFont[prefixFont.length - 1];
      lastFontH = Number(lastFontRaw.match(/,(\d+)/)?.[1] ?? lastFontH);
    }
    const metrics = fieldMetrics(match[0], lastFontH);
    const fontRaw = metrics.fontRaw || lastFontRaw;
    const dummy = metrics.kind === "text" && (!metrics.text || metrics.text === "") && metrics.w <= 8;
    prevEnd = match.index + match[0].length;
    if (dummy) continue;
    n += 1;
    fields.push({
      id: `item-${n}`,
      kind: metrics.kind,
      x: Number(match[1]),
      y: Number(match[2]),
      size: metrics.size,
      fontRaw,
      text: metrics.text,
      start: match.index,
      end: prevEnd,
      prefixStart: match.index - between.length,
      w: metrics.w,
      h: metrics.h,
    });
    if (metrics.fontRaw) {
      lastFontRaw = metrics.fontRaw;
      lastFontH = metrics.size;
    }
  }
  return fields;
}

function replaceFontSize(block: string, size: number): string {
  return block.replace(/\^A(?:@|Z|0|L)([NRIB]),\d+,\d+/g, (full, ori) => {
    if (full.startsWith("^A@")) return `^A@${ori},${size},${size}`;
    if (full.startsWith("^AZ")) return `^AZ${ori},${size},${size}`;
    if (full.startsWith("^AL")) return `^AL${ori},${size},${size}`;
    if (full.startsWith("^A0")) return `^A0${ori},${size},${size}`;
    return `${full.slice(0, 3)}${ori},${size},${size}`;
  });
}

export function applyZplLayoutPatches(zpl: string, patches: ZplLayoutPatch[]): string {
  const fields = parseZplLayoutFields(zpl);
  const byId = new Map(patches.map((p) => [p.id, p]));
  const pieces: string[] = [];
  let cursor = 0;
  for (const field of fields) {
    const patch = byId.get(field.id);
    if (!patch) continue;
    const x = Number.isFinite(patch.x) ? Math.round(patch.x as number) : field.x;
    const y = Number.isFinite(patch.y) ? Math.round(patch.y as number) : field.y;
    const size = Number.isFinite(patch.size) ? Math.max(1, Math.round(patch.size as number)) : field.size;
    const prefix = zpl.slice(Math.max(cursor, field.prefixStart), field.start);
    const raw = zpl.slice(field.start, field.end);
    pieces.push(zpl.slice(cursor, Math.max(cursor, field.prefixStart)));
    let nextPrefix = prefix;
    let nextRaw = raw.replace(/^\^FO-?\d+,-?\d+/, `^FO${x},${y}`);
    if (size !== field.size) {
      if (field.kind === "text") {
        nextPrefix = replaceFontSize(nextPrefix, size);
        nextRaw = replaceFontSize(nextRaw, size);
      } else if (field.kind === "barcode") {
        nextRaw = nextRaw.replace(/\^BX([NRIB]),\d+/, `^BX$1,${size}`);
      } else if (field.kind === "box") {
        nextRaw = nextRaw.replace(/\^GB(\d+),(\d+)/, (_, w, h) => {
          if (Number(h) <= 8 || Number(w) <= 8) return `^GB${w},${h}`;
          const scale = size / field.size;
          return `^GB${Math.max(1, Math.round(Number(w) * scale))},${Math.max(1, Math.round(Number(h) * scale))}`;
        });
      }
    }
    pieces.push(nextPrefix, nextRaw);
    cursor = field.end;
  }
  pieces.push(zpl.slice(cursor));
  return pieces.join("");
}

export function listZplLayoutItems(zpl: string): ZplLayoutItem[] {
  return parseZplLayoutFields(zpl).map((field, index) => ({
    id: field.id,
    name: field.text.trim() ? field.text.slice(0, 48) : `Item ${index + 1}`,
    kind: field.kind,
    x: field.x,
    y: field.y,
    size: field.size,
    font: displayFont(field.fontRaw),
    text: field.text.slice(0, 80),
  }));
}

/** Moves `^FO` (and nearby `^AZ` size) in a Handlebars source using rendered-item ids. */
export function applyLayoutMovesToTemplateSource(
  source: string,
  original: readonly Pick<ZplLayoutItem, "id" | "x" | "y" | "size">[],
  updated: readonly Pick<ZplLayoutItem, "id" | "x" | "y" | "size">[],
): string {
  const byId = new Map(updated.map((item) => [item.id, item]));
  let out = source;
  for (const orig of original) {
    const next = byId.get(orig.id);
    if (next == null) continue;
    const toX = Math.round(next.x);
    const toY = Math.round(next.y);
    const toSize = Math.max(1, Math.round(next.size));
    if (orig.x !== toX || orig.y !== toY) {
      const from = `^FO${String(orig.x)},${String(orig.y)}`;
      const to = `^FO${String(toX)},${String(toY)}`;
      const at = out.indexOf(from);
      if (at >= 0) out = `${out.slice(0, at)}${to}${out.slice(at + from.length)}`;
    }
    if (orig.size !== toSize) {
      const fo = `^FO${String(toX)},${String(toY)}`;
      const foAt = out.indexOf(fo);
      if (foAt >= 0) {
        out = `${replaceLastMatchingAzSize(out.slice(0, foAt), orig.size, toSize)}${out.slice(foAt)}`;
      }
    }
  }
  return out;
}

function replaceLastMatchingAzSize(prefix: string, fromSize: number, toSize: number): string {
  const re = /\^A(@|Z|0|L)([NRIB]),(\d+),(\d+)/g;
  let last: RegExpExecArray | null = null;
  let match: RegExpExecArray | null;
  while ((match = re.exec(prefix))) {
    if (Number(match[3]) === fromSize) last = match;
  }
  if (last == null || last.index == null) return prefix;
  const cmd = last[1] === "@" ? "^A@" : `^A${last[1]}`;
  const next = `${cmd}${last[2]},${String(toSize)},${String(toSize)}`;
  return `${prefix.slice(0, last.index)}${next}${prefix.slice(last.index + last[0].length)}`;
}
